import { LitElement, html } from 'lit';
import { API_ENDPOINT } from './constants';
import ModelMeAIAnalytics from '../service/ModelMeAiAnalyticsService';
import '../components/AuthPrompt';

export class MyPlanPage extends LitElement {
  createRenderRoot() {
    return this; // Allows Tailwind and DaisyUI styling to apply globally
  }

  static properties = {
    currentPlan: { type: String },
    isSignedIn: { type: Boolean },
    showSignInForm: { type: Boolean },
    emailInput: { type: String },
    passwordInput: { type: String },
    showAuthPrompt: { type: Boolean },
  };

  constructor() {
    super();
    this.currentPlan = '';
    this.isSignedIn = false;
    this.showSignInForm = true;
    this.emailInput = '';
    this.passwordInput = '';

    this.showAuthPrompt = false;
    this.checkUserSession();
  }

  async checkUserSession() {
    try {
      const res = await fetch(`${API_ENDPOINT}/api/me`, { credentials: 'include' });
      if (res.ok) {
        this.isSignedIn = true;
        const data = await res.json();
        this.currentPlan = data.plan || 'None';
      } else {
        this.isSignedIn = false;
      }
    } catch (e) {
      console.error('User session check failed:', e);
      this.isSignedIn = false;
    }
  }

  async subscribePlan(plan: string) {
    ModelMeAIAnalytics.getPlausible()?.trackEvent('click_subscribe_plan', { props: { plan } })
    try {
      const res = await fetch(`${API_ENDPOINT}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan }),
      });
      if (!res.ok) {
        throw new Error('Failed to create checkout session');
      }
      const data = await res.json();
      window.location.href = data.url; // Stripe Checkout URL
    } catch (err) {
      console.error('Error subscribing plan:', err);
      alert('Error subscribing plan');
    }
  }

  renderPricingSection() {
    return html`
      <!-- Pricing Hero -->
      <section class="bg-primary text-primary-content py-20">
        <div class="container mx-auto px-4 text-center">
          <h1 class="text-4xl md:text-5xl font-bold mb-4 animate-fade-in">Your Plan</h1>
          <p class="max-w-2xl mx-auto text-lg md:text-xl">
            Upgrade to unlock more image generations and premium features.
          </p>
          <p class="mt-4">Current Plan: <span class="badge badge-secondary badge-lg">${this.currentPlan}</span></p>
        </div>
      </section>

      <!-- Pricing Plans -->
      <section class="py-16">
        <div class="container mx-auto px-4">
          <div class="grid md:grid-cols-3 gap-8">
            ${this.renderPlanCard('Basic', '$9', 'A great starting point for casual creators.', 'basic', [
              'High Usage Cap',
              'Access to all outfits',
              'Custom outfits',
            ])}
            ${this.renderPlanCard('Plus', '$29', 'Ideal for high-volume creators and small agencies.', 'plus', [
              'Increased Usage Cap',
              'Nudify',
              'Priority Support'
            ], true)}
            ${this.renderPlanCard('Pro', '$99', 'Best for power users and creative agencies with high output needs.', 'pro', [
              'Unlimited Usage Cap',
              'Morph Model Body',
              'Change Model Pose',
              'Change Viewing Angle',
              'Generate Short Video',
              'Custom Tools'
            ])}
          </div>
        </div>
      </section>
    `;
  }

  renderPlanCard(title, price, description, planKey, features, isPopular = false) {
    return html`
      <div class="card bg-base-100 border border-base-200 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 relative">
        <div class="card-body text-center justify-between">
          <div>
            ${isPopular ? html`<div class="badge badge-secondary absolute top-4 right-4">Most Popular</div>` : ''}
            <h2 class="card-title text-xl justify-center">${title}</h2>
            <p class="text-4xl font-bold text-primary mb-4">${price}<span class="text-base font-normal text-base-content/70">/mo</span></p>
            <p class="text-base-content/70 mb-6">${description}</p>
            <ul class="text-left space-y-2 text-base-content/70 mb-8">
              ${features.map(feature => html`
                <li class="flex items-center gap-2">
                  <svg class="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  ${feature}
                </li>
              `)}
            </ul>
          </div>
          <button 
            class="btn btn-primary btn-wide rounded-full shadow-md hover:shadow-lg transition-all" 
            @click=${() => this.subscribePlan(planKey)}>
            Choose Plan
          </button>
        </div>
      </div>
    `;
  }

  renderAuthSection() {
    return html`
      <div class="max-w-md mx-auto card bg-base-200 shadow-xl p-8 mt-10">
        <h2 class="text-2xl font-semibold mb-4">${this.showSignInForm ? 'Sign In' : 'Create an Account'}</h2>
        <input type="email" placeholder="Email" class="input input-bordered w-full mb-4" .value=${this.emailInput} @input=${e => this.emailInput = e.target.value}>
        <input type="password" placeholder="Password" class="input input-bordered w-full" .value=${this.passwordInput} @input=${e => this.passwordInput = e.target.value}>
        <button class="btn btn-primary w-full mt-4" @click=${this.handleEmailAuth}>
          ${this.showSignInForm ? 'Sign In' : 'Sign Up'}
        </button>
        <div class="divider my-4">or</div>
        <button class="btn btn-outline btn-secondary w-full" @click=${this.handleGoogleSignIn}>
          Sign in with Google
        </button>
        <p class="mt-4 text-center text-sm cursor-pointer" @click=${() => this.showSignInForm = !this.showSignInForm}>
          ${this.showSignInForm ? 'No account yet?' : 'Already have an account?'}
          <span class="text-blue-500">${this.showSignInForm ? 'Sign Up' : 'Sign In'}</span>
        </p>
      </div>
    `;
  }

  handleEmailAuth() {
    const auth = getAuth();
    const email = this.emailInput.trim();
    const password = this.passwordInput.trim();
    const action = this.showSignInForm ? 'signInWithEmailAndPassword' : 'createUserWithEmailAndPassword';
    import('firebase/auth').then(module => {
      module[action](auth, email, password)
        .then(userCredential => userCredential.user.getIdToken())
        .then(token => fetch(`${API_ENDPOINT}/api/auth`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken: token }),
        }))
        .then(() => location.reload())
        .catch(err => alert(`Authentication failed: ${err.message}`));
    });
  }

  handleEmailAuth() {
    this.handleEmailAuth();
  }

  render() {
    return html`
      <div class="min-h-screen">
        <auth-prompt
          ?isOpen=${this.showAuthPrompt}
          @auth-success=${() => {
            this.showAuthPrompt = false;
            this.isSignedIn = true;
            this.checkUserSession();
          }}
        ></auth-prompt>
        ${this.isSignedIn ? this.renderPricingSection() : this.renderAuthSection()}
      </div>
      <style>
        .animate-fade-in {
          animation: fadeIn 0.5s ease-in;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      </style>
    `;
  }
}

customElements.define('my-plan-page', MyPlanPage);

